import React, { Component } from 'react';
import userStore from '../../store/userStore';
import { isLocalhost } from '../../utils/CommonUtils';
import { getTokenWithEmail } from '../../common/constant';
import App_data from '../../views/config/App_data';
import { aiTutorGlobalContext, refreshAiTutorAvailabilityPublic, isAiTutorKillSwitchedOff, setAiTutorOffState } from '../../common/aiTutorContext';
import FD from '../../views/RestAPI/fetchData';
import { trackAddToCart } from '../../common/analytics';
/* eslint-disable */

// deep-chat is loaded at RUNTIME from public/vendor → webpack never sees it.
// Do NOT `import 'deep-chat'` anywhere webpack compiles (CRA 0.9.5 / webpack 1
// cannot transpile its modern ESM). The browser executes the prebuilt bundle.
const DEEPCHAT_SRC =
  (process.env.PUBLIC_URL || '') + '/vendor/deepChat-2.4.2.bundle.js';
// Resolve the AI Tutor service base URL.
// Prefer REACT_APP_TUTOR_URL (base origin), keep compatibility with legacy
// values ending in /tutor/chat, fall back to localhost during local
// development, then App_data.backend_api (same-origin reverse proxy).
const normalizeTutorBase = (value) =>
  value
    ? String(value)
        .trim()
        .replace(/\/tutor\/chat\/?$/i, '')
        .replace(/\/+$/, '')
    : null;

const getTutorBase = () => {
  const envBase = normalizeTutorBase(process.env.REACT_APP_TUTOR_URL);

  if (envBase) return envBase;

  if (isLocalhost) return 'http://localhost:8880';

  // NB: no optional chaining — react-scripts 0.9.5 / Babel does not transpile `?.`
  // and it breaks the whole bundle at build time.
  return typeof App_data !== 'undefined' && App_data
    ? normalizeTutorBase(App_data.backend_api)
    : null;
};

const TUTOR_BASE = getTutorBase();
const TUTOR_URL = TUTOR_BASE ? `${TUTOR_BASE}/tutor/chat` : null;
const TUTOR_CONFIGURED = Boolean(TUTOR_BASE);

// ---- V6 "Indigo pro" theme tokens (from the approved MVP mockup) ----
// Violet "AI Tutor" identity (design 2026-07-08 — AnalystPrep AI Tutor Widget_02):
// one visual language, violet + ✦, distinct from LMS blue and Intercom grey.
const T = {
  accent: '#4a2fe0',
  accentSoft: '#ece9fd',
  subText: '#8a86a8',
  panelBg: '#ffffff',
  bodyBg: '#f6f6fc',
  aiBubbleBg: '#f2f0fd',
  aiBubbleText: '#241f45',
  aiBubbleBorder: '#e6e1fb',
  userBubbleBg: 'linear-gradient(135deg,#6d4bff,#4a2fe0)',
  chipBg: '#f6f4fe',
  chipText: '#4a2fe0',
  chipBorder: '#e2ddf8',
  inputBg: '#ffffff',
  inputBorder: '#e2e6f5',
  footText: '#9a97ad',
  headerBg: 'linear-gradient(135deg,#6d4bff 0%,#4a2fe0 100%)',
  headerBadge: '#4a2fe0',
  subBarBg: '#f7f6fe',
  subBarBorder: '#eceafb',
  ctxText: '#4a2fe0',
  meterTrack: '#e6e1fb',
  font: "'Plus Jakarta Sans', system-ui, sans-serif",
};

let _loaderPromise = null;
const loadDeepChat = () => {
  if (window.customElements && customElements.get('deep-chat')) {
    return Promise.resolve();
  }
  if (_loaderPromise) return _loaderPromise;
  _loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.type = 'module';
    s.src = DEEPCHAT_SRC;
    s.onload = resolve;
    s.onerror = () => { reject(new Error('deep-chat bundle failed to load')); };
    document.head.appendChild(s);
  }).then(() => { return customElements.whenDefined('deep-chat'); });
  return _loaderPromise;
}

// deep-chat's KaTeX integration needs window.katex present before math renders.
// Loaded once, lazily. (Prod: self-host katex + fonts; CDN is fine for dev.)
let _katexPromise = null;
const loadKatex = () => {
  if (window.katex) return Promise.resolve();
  if (_katexPromise) return _katexPromise;
  _katexPromise = new Promise((resolve) => {
    try {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css';
      document.head.appendChild(link);
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js';
      s.onload = () => resolve();
      s.onerror = () => resolve(); // never block the chat if katex fails to load
      document.head.appendChild(s);
    } catch (e) { resolve(); }
  });
  return _katexPromise;
}

const getAuthToken = () => {
  try { return getTokenWithEmail().token; } catch (e) { return null; }
}

// Forward-looking entitlement check. NOTE: the backend does not expose
// `tutorStatus` yet (it's the P7 server-side entitlement work), so this returns
// false today for everyone. `subscription_details` can also arrive as a JSON
// string — parse it defensively.
const hasTutorEntitlement = (subs) => {
  if (typeof subs === 'string') {
    try { subs = JSON.parse(subs); } catch (e) { return false; }
  }
  if (!subs || !subs.length) return false;
  return subs.some((s) => { return s && s.pacakge && s.pacakge.tutorStatus; });
}

// intro card (greeting + starter chips) as light-DOM child markup deep-chat slots in.
// .deep-chat-suggestion-button is auto-wired to submit the chip text on click.
// Chips depend on where the chat was opened (page_type at first mount): the label IS the
// submitted prompt, grounded by the page `stem` the requestInterceptor attaches.
const introChips = (pageType) => {
  if (pageType === 'question') {
    return ['Explain my answer', 'Why is this answer correct?', 'Explain simply', 'Give me an example'];
  }
  if (pageType === 'note') {
    return ['Summarize this note', 'Explain simply', 'Give me an example', 'Test me on this'];
  }
  if (pageType === 'video') {
    return ['Summarize the key ideas of this lecture', 'Explain simply', 'Give me an example'];
  }
  // global / unknown page: generic CFA L1 prompts
  return ['Explain this concept', 'Explain simply', 'Give me an example'];
}
const introSubject = (pageType) => {
  if (pageType === 'question') return 'this question';
  if (pageType === 'note') return 'this note';
  if (pageType === 'video') return 'this lecture';
  return 'the CFA L1 curriculum';
}
const introHTML = (ctx) => {
  const pageType = (ctx && ctx.page_type) || 'global';
  const chip =
    'padding:9px 13px;border:1px solid ' + T.chipBorder + ';border-radius:11px;background:' +
    T.chipBg + ';color:' + T.chipText + ";font:700 13px " + T.font + ';cursor:pointer;text-align:left;';
  const chips = introChips(pageType)
    .map((label) => {
      return '<button class="deep-chat-suggestion-button" style="' + chip + '">' + label + '</button>';
    })
    .join('');
  return (
    '<div style="padding:14px">' +
      '<div style="background:' + T.aiBubbleBg + ';border:1px solid ' + T.aiBubbleBorder +
        ';color:' + T.aiBubbleText + ';border-radius:4px 15px 15px 15px;padding:12px 14px;font:500 13.5px ' + T.font +
        ';line-height:1.5;">Hi \ud83d\udc4b Ask me about ' + introSubject(pageType) +
        ' \u2014 I answer from the CFA L1 curriculum and cite the source.</div>' +
    '<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:12px">' + chips + '</div>' +
    '</div>'
  );
}

const escHtml = (s) => {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
// climb from the clicked node to the element carrying `attr` (deep-chat click
// target may be an inner text node / span).
const climbAttr = (node, attr) => {
  while (node) {
    if (node.getAttribute) { const v = node.getAttribute(attr); if (v != null) return v; }
    node = node.parentNode;
  }
  return null;
}

// V6 "Indigo" icons (shadow-DOM-safe inline SVG) — from the approved spike design
const V6_ICON = {
  doc: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h3"/></svg>',
  book: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 6c-1.5-1.2-4-2-7-2v13c3 0 5.5.8 7 2 1.5-1.2 4-2 7-2V4c-3 0-5.5.8-7 2zM12 6v13"/></svg>',
  play: '<svg width="15" height="15" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="#fff"/></svg>',
};

// Composer placeholder copy (design Widget_03). Period-aware like the pill/upsell card — the
// allowance can be day/month/total, so a monthly cap must not read "Daily".
const INPUT_PLACEHOLDER = 'Ask a follow-up…';
const cappedPlaceholder = (period) => {
  if (period === 'month') return 'Monthly limit reached — upgrade for more';
  if (period === 'total') return 'Limit reached — upgrade for more'; // lifetime — no reset to promise
  return 'Daily limit reached — upgrade for more';
}

// KB `content_type` → the icon/wording used by BOTH "Based on" and READ NEXT, so a source
// looks the same wherever it appears. `type` comes from the backend citation/readmore entry
// (note | video | question | …); anything unknown falls back to a document.
const sourceKind = (rawType) => {
  const t = String(rawType || '').toLowerCase();
  if (t === 'video' || t === 'lecture') return { icon: 'play', cta: 'Play', sub: 'Lecture · watch' };
  if (t === 'question' || t === 'quiz') return { icon: 'doc', cta: 'Start', sub: 'Practice · start' };
  if (t === 'note' || t === 'reading') return { icon: 'book', cta: 'Read', sub: 'Reading · jump to source' };
  return { icon: 'doc', cta: 'Open', sub: 'Source · open' };
}

// 1) "Based on" — grounding proof, MINIMISED to one "Based on N sources" line so it never
// competes with the answer; hover (or keyboard focus — tabindex + :focus-within) reveals the
// source list in a popover. Design 2026-07-20 (Widget_03) BLOCK 1.
const BO_DB_ICON =
  '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" style="opacity:.7;flex:none"><ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5"/><path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3"/></svg>';
const BO_CHEV_ICON =
  '<svg class="dc-basedon-chev" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" style="flex:none"><path d="M6 9l6 6 6-6"/></svg>';

const basedOnHtml = (cits) => {
  if (!cits.length) return '';
  const rows = cits
    .map((c) => {
      const label = escHtml((c && (c.label || c.uri)) || '');
      // per-source icon + action word, shared with READ NEXT (Read / Play / Start / Open)
      const kind = sourceKind(c && c.type);
      const icCls = kind.icon === 'play' ? 'dc-bo-ic dc-bo-ic-play' : 'dc-bo-ic';
      const inner =
        '<span class="' + icCls + '">' + (V6_ICON[kind.icon] || V6_ICON.doc) + '</span>' +
        '<span class="dc-bo-lbl">' + label + '</span>' +
        (c && c.url ? '<span class="dc-bo-go">' + kind.cta + ' \u2197</span>' : '');
      return c && c.url
        ? '<a class="dc-bo-item" href="' + escHtml(c.url) + '" target="_blank" rel="noopener">' + inner + '</a>'
        : '<span class="dc-bo-item">' + inner + '</span>';
    })
    .join('');
  const noun = cits.length === 1 ? 'source' : 'sources';
  return (
    '<div class="dc-basedon" tabindex="0">' +
      '<div class="dc-basedon-sum">' + BO_DB_ICON +
        '<span>Based on <strong>' + cits.length + '</strong> ' + noun + '</span>' + BO_CHEV_ICON +
      '</div>' +
      '<div class="dc-basedon-pop"><div class="dc-bo-hd">GROUNDED IN</div>' + rows + '</div>' +
    '</div>'
  );
}

// 2) "READ NEXT" — study-next cards (spike: dc-srccard). Only render items the backend
// resolved to a real source link ({label,url,type} → Open/Play). Unresolved model titles
// (plain strings, no URL) are dropped — no "Ask" fallback; if nothing resolves, no block.
const readNextHtml = (items) => {
  // Keep only url-bearing items, deduped by url (defensive — the backend dedups too, but an
  // older backend or a repeated recommendation must never render the same card twice).
  const seenUrls = {};
  const resolved = (items || []).filter((r) => {
    if (!(r && typeof r === 'object' && r.url) || seenUrls[r.url]) return false;
    seenUrls[r.url] = 1;
    return true;
  });
  if (!resolved.length) return '';
  const rows = resolved
    .map((r) => {
      const title = escHtml(r.label || r.url || '');
      const kind = sourceKind(r.type); // same icon/action map as "Based on"
      const iconCls = kind.icon === 'play' ? 'dc-srcicon dc-srcicon-play' : 'dc-srcicon';
      const inner =
        '<span class="' + iconCls + '">' + (V6_ICON[kind.icon] || V6_ICON.doc) + '</span>' +
        '<span class="dc-srctext"><span class="dc-srctitle">' + title + '</span><span class="dc-srcsub">' + kind.sub + '</span></span>' +
        '<span class="dc-srccta">' + kind.cta + ' →</span>';
      return '<a class="dc-srccard" href="' + escHtml(r.url) + '" target="_blank" rel="noopener">' + inner + '</a>';
    })
    .join('');
  return '<div class="dc-readnext-lbl">READ NEXT</div><div class="dc-srccards">' + rows + '</div>';
}

// 3) "SUGGESTED FOLLOW-UPS" — native deep-chat suggestion buttons (click auto-submits the
// label as the next question); full-width V6 styling scoped via .dc-followups in auxiliaryStyle.
const followupsHtml = (fups) => {
  if (!fups.length) return '';
  const btns = fups
    .map((f) => {
      return '<button class="deep-chat-suggestion-button">' + escHtml(String(f)) + ' \u2192</button>';
    })
    .join('');
  return '<div class="dc-followups"><div class="dc-followups-lbl">SUGGESTED FOLLOW-UPS</div>' + btns + '</div>';
}

// 4) "Was this helpful?" — 👍/👎 targeting the exact tutor_message row (data-mid = the
// SSE messageId). Rendered only when the backend confirmed the row was persisted.
const feedbackRowHtml = (messageId) => {
  const up =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 11v9M2 12h5v8H2zM7 11l4-8a2 2 0 0 1 2.7 1.5l-1 5.5h6a2 2 0 0 1 2 2.4l-1.5 6A2 2 0 0 1 17 20H7"/></svg>';
  const down =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 13V4M22 12h-5V4h5zM17 13l-4 8a2 2 0 0 1-2.7-1.5l1-5.5H5.3a2 2 0 0 1-2-2.4l1.5-6A2 2 0 0 1 7 4h10"/></svg>';
  return (
    '<div class="dc-fbrow" data-mid="' + escHtml(messageId) + '">' +
      '<span class="dc-fb-label">Was this helpful?</span>' +
      '<button type="button" class="dc-fb dc-fb-up" aria-label="Helpful">' + up + '</button>' +
      '<button type="button" class="dc-fb dc-fb-down" aria-label="Not helpful">' + down + '</button>' +
    '</div>'
  );
}

// Per the 2026-07-20 design (Widget_03) the answer bubble CONTAINS its grounding + actions:
//   [ answer text · Based on N sources · READ NEXT · Was this helpful? ]   ← one violet bubble
//   SUGGESTED FOLLOW-UPS                                                   ← outside, frameless
// deep-chat can't append into an already-streamed message, so the extras ride in a second
// message with role 'extras' whose bubble is styled as a seamless continuation of the answer
// (same fill, no top border, square top, negative margin) — see el.messageStyles.
const answerExtrasHTML = (x) => {
  const citations = x.citations || [];
  const readmore = x.readmore || [];
  let out = '<div style="font-family:' + T.font + '">';
  out += basedOnHtml(citations);
  out += readNextHtml(readmore);
  if (x.messageId) out += feedbackRowHtml(x.messageId);
  return out + '</div>';
}

// Separate, frameless message: the SUGGESTED FOLLOW-UPS label sits on the panel background
// (not inside the answer bubble) because clicking one starts the NEXT turn.
const extrasHTML = (x) => {
  const followups = x.followups || [];
  let out = '<div style="font-family:' + T.font + '">';
  out += followupsHtml(followups);
  return out + '</div>';
}

// Time left until the usage window resets. The server counters are UTC (Redis windows),
// so "tomorrow" means the next UTC midnight, not the browser's local midnight. Returns a
// short label ("7h 12m", "1d 3h", "45m") or null when the window never resets ('total').
const resetCountdown = (period) => {
  try {
    const now = new Date();
    let end;
    if (period === 'total') return null;
    if (period === 'month') end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
    else end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1); // day
    const mins = Math.ceil((end - now.getTime()) / 60000);
    if (!(mins > 0)) return null;
    const days = Math.floor(mins / 1440);
    const hours = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    if (days >= 2) return days + 'd';
    if (days === 1) return '1d ' + hours + 'h';
    if (hours >= 1) return hours + 'h ' + m + 'm';
    return m + 'm';
  } catch (e) { return null; }
}

// Limit-reached upsell card (design 2026-07-14, Placement Studio "Limit" tab).
// V1: the CTA is hardcoded to the CFA1 packages page — make it exam-aware together
// with the launcher gate + selectedmenu→exam map when the tutor expands (V2+).
const UPGRADE_URL = '/subscribe/selectPackage/cfa1/1';
const upgradeHTML = (limit, period) => {
  const windowWord = period === 'month' ? ' this month' : period === 'total' ? '' : ' today';
  const title = (limit > 0 ? "You've used all " + limit + ' free questions' : "You've used all your free questions") + windowWord;
  const resetWord = period === 'month' ? 'monthly' : 'daily';
  const sub = period === 'total'
    ? 'Upgrade to <b>Unlimited+</b> for unlimited AI tutoring.'
    : 'Upgrade to <b>Unlimited+</b> for unlimited AI tutoring, or wait for your ' + resetWord + ' reset.';
  const left = resetCountdown(period);
  return '<div class="dc-upgrade">' +
    '<div class="dc-upgrade-title">✦ ' + title + '</div>' +
    '<div class="dc-upgrade-sub">' + sub + '</div>' +
    '<div class="dc-upgrade-row">' +
    '<a class="dc-upgrade-btn" href="' + UPGRADE_URL + '">Upgrade to Unlimited+</a>' +
    (left ? '<span class="dc-upgrade-reset">Resets in ' + left + '</span>' : '') +
    '</div></div>';
}

// Guardrail Change 3 — recoverable block. When the guardrail stops a question, the refusal bubble
// is followed by this card so it's not a dead end: one click hands off to a human (Intercom). The
// (escaped) blocked question rides on data-q to prefill the Intercom conversation.
const recoveryHTML = (question) => {
  const q = escHtml(String(question || ''));
  return '<div style="background:' + T.chipBg + ';border:1px solid ' + T.chipBorder +
      ';border-radius:12px;padding:12px 14px;margin-top:4px">' +
    '<div style="font:600 12.5px ' + T.font + ';color:' + T.aiBubbleText + ';line-height:1.5">' +
      'Still need help with this? A person from our team can take over.</div>' +
    '<button class="tutor-escalate" data-q="' + q + '" ' +
      'style="margin-top:9px;border:none;border-radius:9px;background:' + T.accent +
      ';color:#fff;font:700 12.5px ' + T.font + ';padding:8px 13px;cursor:pointer">' +
      'Talk to a human →</button>' +
  '</div>';
}

// "Upgrade to Unlimited+" click: put the CFA Unlimited+ package (exam_plans.package_val
// = 'plus', id 78) into the server-side
// cart via the same two checkout_v1 endpoints the packages page uses (the backend
// validates ownership and computes the upgrade credit itself), then land in /cart.
// ANY failure — no backend base, request error, plan not found, already owned,
// success:false — falls back to plain navigation to the packages page (UPGRADE_URL),
// i.e. exactly the pre-JS behavior of the card's anchor.
const addUnlimitedToCartAndGo = () => {
  const base = App_data && App_data.backend_api;
  if (!base) { window.location.assign(UPGRADE_URL); return; }
  FD.callAPI(base + '/checkout_v1/getExamPackagesByUserId', { examId: 1, examName: 'cfa1' })
    .then((resp) => {
      const list = (resp && resp.success && resp.packages) || [];
      let plan = null;
      for (let i = 0; i < list.length; i++) {
        const p = list[i];
        if (p && String(p.package_val).toLowerCase() === 'plus' && p.package_category !== 'mock') {
          plan = p;
          break;
        }
      }
      if (!plan || plan.isOwned) { window.location.assign(UPGRADE_URL); return null; }
      const payload = { examId: Number(plan.exam_id), planId: Number(plan.planId), addonIds: [], source: 'ai-tutor' };
      let displayPrice = plan.price;
      if (plan.upgradeAvailable) {
        payload.type = plan.upgradeType || 'upgrade_standard';
        payload.upgradeFromPlanId = plan.upgradeFromPlanId;
        displayPrice = plan.upgradePrice;
        payload.displayPrice = displayPrice;
      } else {
        payload.type = 'main';
      }
      return FD.callAPI(base + '/checkout_v1/addToUserCart', payload).then((r) => {
        if (r && r.success) {
          try {
            // same GTM/GA4 AddToCart event the packages page fires — keeps widget
            // upsells inside the conversion funnel (user fields default to '')
            trackAddToCart({
              exam: 'CFA Level I',
              packageType: plan.package_type || 'CFA Unlimited+ Package',
              value: Number(displayPrice) || 0,
              source: (userStore && userStore.landingSource) || 'organic',
              affiliateName: (userStore && userStore.landingSourceName) || null,
            });
          } catch (e) { /* analytics is best-effort */ }
          window.location.assign('/cart');
        } else {
          window.location.assign(UPGRADE_URL);
        }
      });
    })
    .catch(() => { window.location.assign(UPGRADE_URL); });
}

// ── Draggable panel (design 2026-07-20, Widget_03) ────────────────────────────────────
// Plain mouse events on purpose: React 15 has no synthetic POINTER events (added in 16.4),
// and every modern DnD library (dnd-kit, react-dnd v14+, react-rnd, react-draggable v4)
// requires React >= 16.8 hooks. Dragging is desktop-only — on mobile the panel is a
// full-screen sheet — so mouse events cover every case.
// The offset is PERSISTED: the panel covering page content is the reason it is movable,
// so where the student parks it must survive navigation.
const DRAG_KEY = 'apAiTutorPos';
const DRAG_MARGIN = 8; // keep at least this many px of the panel on screen

const loadDragPos = () => {
  try {
    const raw = localStorage.getItem(DRAG_KEY);
    if (!raw) return { x: 0, y: 0 };
    const p = JSON.parse(raw);
    const x = Number(p && p.x);
    const y = Number(p && p.y);
    return { x: isFinite(x) ? x : 0, y: isFinite(y) ? y : 0 };
  } catch (e) {
    return { x: 0, y: 0 };
  }
}

const saveDragPos = (p) => {
  try {
    localStorage.setItem(DRAG_KEY, JSON.stringify({ x: Math.round(p.x), y: Math.round(p.y) }));
  } catch (e) { /* private mode / quota — dragging still works for this page */ }
}

// Pointer coords from either a mouse or a touch event (React 15 has no pointer events).
const dragPoint = (e) => {
  if (e.touches && e.touches.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  if (e.changedTouches && e.changedTouches.length) {
    return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
  }
  return { x: e.clientX, y: e.clientY };
}

// Mobile = the same breakpoint as the full-screen-sheet media query below.
const isMobileViewport = () => {
  try {
    return !!(window.matchMedia && window.matchMedia('(max-width:574.98px)').matches);
  } catch (e) {
    return false;
  }
}

class AITutorChat extends Component {
  constructor(props) {
    super(props);
    // `killed` = admin kill-switch. Seeded synchronously from the cached flag so a disabled
    // tutor never flashes on repeat loads; refreshed from the public /tutor/health probe.
    this.state = { enabled: false, open: false, notice: null, killed: isAiTutorKillSwitchedOff() };
    this._box = null;
    this._el = null;
    this._ctx = props.context || {};
    this._killedProbeExam = null; // last exam sent to /tutor/health (dedupes mount vs 1st context)
    this._sid = null;
    this._lastMessageId = null; // last persisted answer row — feedback + escalation target
    this._mountedFlag = false;
    this._pendingItem = null;     // attached to the next request body (e.g. "Explain my answer")
    this._ctxLabelRef = null;
    this._usageRef = null; // "✦ N left today" pill — updated imperatively from SSE
    this._upgradeShown = false; // one upsell card per depletion (re-armed on headroom)
    this._lastLimit = null;     // last known cap size (GET /tutor/usage) — card title
    this._lastPeriod = null;    // last known window — the limitReached SSE has no period
    this._pendingUpgradeCard = false; // capped-on-open card queued until deep-chat mounts
    this._capped = false;         // 0 turns left → dimmed composer + dead send button
    this._composerGuard = null;   // capture-phase submit blocker (installed once, on #input)
    this._composerTries = 0;      // deep-chat renders its shadow DOM async — bounded retry
    this._composerTimer = null;
    this._setRef = (el) => { this._box = el; };
    // drag: callback refs (React 15 has no createRef) + the persisted offset
    this._panelEl = null;
    this._dragHandleEl = null;
    this._dragPos = loadDragPos();
    this._setPanelRef = (el) => { this._panelEl = el; };
    this._setDragHandleRef = (el) => { this._dragHandleEl = el; };
    this._onToggle = this._onToggle.bind(this);
    this._onContext = this._onContext.bind(this);
    this._onExplain = this._onExplain.bind(this);
    this._onDragStart = this._onDragStart.bind(this);
  }

  async componentDidMount() {
    this._mountedFlag = true;
    window.addEventListener('aitutor:toggle', this._onToggle);
    window.addEventListener('aitutor:context', this._onContext);
    window.addEventListener('aitutor:explain', this._onExplain);
    const subs = await userStore.getSubscriptionDetails();
    // TODO(P7): gate strictly on hasTutorEntitlement(subs) once the backend
    // exposes `tutorStatus`. Until then, enable for any authenticated user so
    // the feature is testable on this branch.
    const enabled = hasTutorEntitlement(subs) || !!getAuthToken();
    if (this._mountedFlag && enabled) this.setState({ enabled: true });
    const exam = (this._ctx && this._ctx.exam) || 'cfa1';
    // A.2: kill-switch sync. Public /tutor/health only — the old token /tutor/usage probe fired
    // a usage request on every load even when the tutor was disabled.
    this._refreshKilled(exam);
  }

  // Kill-switch pull point — /tutor/health only (public, no token, survives a broken session).
  // Deduped per exam: mount probes the default exam, then the first `aitutor:context` event
  // (prevExam still undefined) would re-probe the SAME exam — one probe per exam is enough.
  _refreshKilled(exam) {
    const key = String(exam || 'cfa1').toLowerCase();
    if (key === this._killedProbeExam) return;
    this._killedProbeExam = key;
    refreshAiTutorAvailabilityPublic(TUTOR_BASE, key, (off) => {
      if (this._mountedFlag) this.setState({ killed: off, open: off ? false : this.state.open });
    });
  }

  componentWillReceiveProps(next) {
    if (this._el && next.context !== this.props.context) this._ctx = next.context || {};
  }

  componentWillUnmount() {
    this._mountedFlag = false;
    window.removeEventListener('aitutor:toggle', this._onToggle);
    window.removeEventListener('aitutor:context', this._onContext);
    window.removeEventListener('aitutor:explain', this._onExplain);
    clearTimeout(this._composerTimer); // bounded retry must not outlive the widget
    this._composerTimer = null;
    this._composerGuard = null;
    if (this._el && this._el.parentNode) this._el.parentNode.removeChild(this._el);
    this._el = null;
  }

  // Mount deep-chat only after the panel is laid out (display:none → flex),
  // otherwise it measures a 0-height host and collapses. rAF defers past layout.
  // Reachability probe: GET /tutor/health on the service base. Resolves on ANY http
  // response (even 404 from an older backend without the endpoint — reachable is
  // enough); rejects only on network error / timeout, i.e. service down or not deployed.
  _probeHealth() {
    const url = TUTOR_BASE + '/tutor/health';
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => { if (!settled) { settled = true; reject(new Error('timeout')); } }, 3000);
      fetch(url).then(
        (r) => { if (!settled) { settled = true; clearTimeout(timer); resolve(r); } },
        (e) => { if (!settled) { settled = true; clearTimeout(timer); reject(e); } }
      );
    });
  }

  _ensureMounted(cb) {
    if (this._el) { if (cb) cb(); return; }
    // Guard 1: no tutor endpoint configured for this env → notice, don't mount.
    if (!TUTOR_CONFIGURED) { this.setState({ notice: 'config' }); return; }
    // Guard 2: endpoint configured but the service is unreachable → notice.
    // Re-probed on every open, so the chat recovers once the service is back.
    this._probeHealth()
      .then(() => Promise.all([loadDeepChat(), loadKatex()])
        .then(() => {
          this.setState({ notice: null }, () =>
            requestAnimationFrame(() => { this._mountDeepChat(); if (cb) cb(); }));
        })
        .catch(() => { this.setState({ notice: 'load' }); })) // bundle failed to load
      .catch(() => { this.setState({ notice: 'backend' }); }); // service down / keys missing
  }

  _onToggle() {
    if (!this.state.enabled) return;
    if (this.state.killed || isAiTutorKillSwitchedOff()) return; // disabled → don't open/fetch
    const open = !this.state.open;
    // pull the current page context on open (robust against event-timing — the
    // page writes window.__aiTutorContext; the `aitutor:context` event only
    // live-updates while already open).
    if (open) this._ctx = window.__aiTutorContext || aiTutorGlobalContext();
    this.setState({ open }, () => {
      if (open) { this._settleDragPos(); this._ensureMounted(); this._fetchUsage(); }
    });
  }

  // Keep the panel on screen: `base` is its position WITHOUT any transform, so the clamp
  // works no matter where the CSS anchors it (and after a viewport resize).
  _clampDrag(x, y, base) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minX = DRAG_MARGIN - base.left;
    const maxX = vw - DRAG_MARGIN - base.w - base.left;
    const minY = DRAG_MARGIN - base.top;
    const maxY = vh - DRAG_MARGIN - base.h - base.top;
    return {
      x: Math.min(Math.max(x, minX), Math.max(minX, maxX)),
      y: Math.min(Math.max(y, minY), Math.max(minY, maxY)),
    };
  }

  _applyDragTransform() {
    const el = this._panelEl;
    if (!el) return;
    const p = this._dragPos;
    el.style.transform = p.x || p.y ? 'translate(' + p.x + 'px,' + p.y + 'px)' : '';
  }

  // Re-clamp on open: the saved offset may be off-screen on a smaller window / another page.
  _settleDragPos() {
    const el = this._panelEl;
    if (!el) return;
    if (isMobileViewport()) { el.style.transform = ''; return; } // sheet: never offset
    const p = this._dragPos;
    const r = el.getBoundingClientRect();
    this._dragPos = this._clampDrag(p.x, p.y, { left: r.left - p.x, top: r.top - p.y, w: r.width, h: r.height });
    this._applyDragTransform();
  }

  // Header drag. Moves the element imperatively (no setState per frame — a re-render on every
  // mousemove would tear down deep-chat's DOM); the final offset is committed on mouseup.
  _onDragStart(e) {
    // Mobile is a full-screen sheet — nothing to drag. The header still carries
    // touch-action:none, so the gesture dies here instead of scrolling the page.
    if (isMobileViewport()) return;
    const isTouch = !!(e.touches && e.touches.length);
    if (!isTouch && e.button !== 0) return; // left mouse button only
    if (e.target && e.target.closest && e.target.closest('[data-no-drag]')) return; // ✕ etc.
    const el = this._panelEl;
    if (!el) return;
    if (e.preventDefault) e.preventDefault(); // no text selection / no page pan

    const start = this._dragPos;
    const r = el.getBoundingClientRect();
    const base = { left: r.left - start.x, top: r.top - start.y, w: r.width, h: r.height };
    const p0 = dragPoint(e);
    let pending = start;

    const move = (ev) => {
      // touchmove is registered non-passive so this can cancel the browser's pan
      if (ev.cancelable) ev.preventDefault();
      const p = dragPoint(ev);
      pending = this._clampDrag(start.x + (p.x - p0.x), start.y + (p.y - p0.y), base);
      el.style.transform = 'translate(' + pending.x + 'px,' + pending.y + 'px)';
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchcancel', up);
      document.body.style.userSelect = '';
      if (this._dragHandleEl) this._dragHandleEl.style.cursor = 'grab';
      this._dragPos = pending;
      saveDragPos(pending);
    };

    document.body.style.userSelect = 'none';
    if (this._dragHandleEl) this._dragHandleEl.style.cursor = 'grabbing';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    window.addEventListener('touchcancel', up);
  }

  _ctxText() {
    return 'Context · ' + ((this._ctx && (this._ctx.title || this._ctx.content_id)) || 'CFA Level I');
  }
  // V6 usage pill (spike design): "✦ N left today" · "✦ Unlimited" · amber "0 left · resets soon".
  // Imperative DOM update (like the context label) so it never collides with React reconciliation.
  // `period` ('day' | 'month' | 'total') tunes the wording; missing/day → "today".
  _updateUsage(remaining, period) {
    if (period) this._lastPeriod = period;
    // headroom again (new window / admin raise / unlimited) → allow a future card
    if (remaining === null || remaining === undefined || Number(remaining) > 0) this._upgradeShown = false;
    // Same signal as the pill (unlimited = null → never capped). BEFORE the pill's early
    // return, or a missing pill ref strands the composer. Re-enables on any headroom refresh.
    this._setComposerCapped(
      remaining !== null && remaining !== undefined && !(Number(remaining) > 0)
    );
    const el = this._usageRef;
    if (!el) return;
    el.style.display = 'inline-flex';
    const violet = () => { el.style.color = T.accent; el.style.background = T.accentSoft; el.style.border = 'none'; };
    if (remaining === null || remaining === undefined) {
      el.textContent = '✦ Unlimited';
      violet();
      return;
    }
    const n = Number(remaining);
    if (!(n > 0)) {
      // 'total' is a lifetime cap — there is no reset to promise.
      const cd = resetCountdown(period);
      el.textContent = period === 'total' ? '0 left' : '0 left · resets ' + (cd ? 'in ' + cd : 'soon');
      el.style.color = '#c2410c'; el.style.background = '#fff1e6'; el.style.border = '1px solid #ffd9bd';
      return;
    }
    const suffix = period === 'month' ? ' left this month'
      : period === 'total' ? ' left'
      : ' left today';
    el.textContent = '✦ ' + n + suffix;
    violet();
  }

  // ---- capped composer (design Widget_03): placeholder swap + 0.55 dim + dead send button ----
  // Verified headlessly against the vendored bundle; don't "simplify" without re-probing:
  //  1. Placeholder = the `deep-chat-placeholder-text` ATTRIBUTE (`content:attr()` on
  //     `#text-input:empty::before`). Re-assigning `el.textInput` after mount blanks it.
  //  2. Opacity goes on `#input` — the only common ancestor of the box and the submit button
  //     (the button is an absolutely-positioned sibling of `#text-input-container`).
  //  3. The Enter guard must sit on `#input`, not `#text-input`: deep-chat registers its own
  //     keydown on that node first, so a same-node stopImmediatePropagation loses.
  _setComposerCapped(capped) {
    this._capped = !!capped; // before any DOM work — the guard reads it even pre-mount
    this._applyComposerState();
  }

  _installComposerGuard(input) {
    if (this._composerGuard) return; // once per deep-chat instance
    const guard = (e) => {
      if (!this._capped) return;
      if (e.type === 'keydown' && (e.key !== 'Enter' || e.shiftKey)) return; // shift+Enter = newline
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    this._composerGuard = guard;
    input.addEventListener('keydown', guard, true);
    input.addEventListener('click', guard, true);
  }

  // deep-chat renders its shadow DOM async — retry briefly (same race as _pendingUpgradeCard).
  _applyComposerState() {
    const sr = this._el && this._el.shadowRoot;
    const input = sr && sr.getElementById('input');
    const ti = sr && sr.getElementById('text-input');
    if (!input || !ti) {
      if (this._composerTries < 20) {
        this._composerTries++;
        clearTimeout(this._composerTimer);
        this._composerTimer = setTimeout(() => this._applyComposerState(), 100);
      }
      return;
    }
    this._composerTries = 0;
    this._installComposerGuard(input);
    if (this._capped) input.classList.add('ap-capped');
    else input.classList.remove('ap-capped');
    ti.setAttribute('contenteditable', this._capped ? 'false' : 'true'); // no typing while capped
    // A typed draft leaves the box non-`:empty`, so the placeholder stays hidden until the
    // student clears it — their text is deliberately not destroyed.
    ti.setAttribute(
      'deep-chat-placeholder-text',
      this._capped ? cappedPlaceholder(this._lastPeriod) : INPUT_PLACEHOLDER
    );
  }

  // Read-only usage snapshot (GET /tutor/usage) — how many questions remain / unlimited, WITHOUT
  // spending a turn. Called on every open and on course/context switch so the pill is always
  // current, even before the first message. Best-effort: any failure leaves the pill untouched.
  _fetchUsage() {
    if (!TUTOR_BASE) return;
    if (this.state.killed || isAiTutorKillSwitchedOff()) return; // disabled → no usage request
    const examKey = ((this._ctx && this._ctx.exam) || 'CFA1').toString().toLowerCase();
    // cache-buster + no-store: the count must reflect admin changes on the very next reload,
    // never a browser-cached copy of this GET.
    const url = TUTOR_BASE + '/tutor/usage?exam=' + encodeURIComponent(examKey) + '&_=' + Date.now();
    fetch(url, { headers: { Authorization: getAuthToken() }, cache: 'no-store' }).then(
      (r) => {
        // An auth/server error (401 "token invalid", 5xx) is NOT a kill-switch signal. Its JSON
        // body has no `enabled`, so parsing it would evaluate `undefined === false` → false and
        // CLEAR a real admin disable on every load. Bail out: leave the cached state untouched.
        if (!r.ok) return null;
        return r.json().then(
        (d) => {
          if (!d) return;
          setAiTutorOffState(d.enabled === false); // panel-open pull point (no extra request)
          if (d.enabled === false) { this.setState({ notice: 'disabled' }); return; }
          if (this.state.notice === 'disabled') this.setState({ notice: null });
          if (d.limit !== undefined && d.limit !== null) this._lastLimit = Number(d.limit);
          this._updateUsage(d.unlimited ? null : d.remaining, d.period);
          // already capped when the panel opens → show the upsell card under the intro
          // (dedup via the same _upgradeShown flag as the SSE triggers)
          if (!d.unlimited && Number(d.remaining) === 0 && !this._upgradeShown) {
            this._upgradeShown = true;
            if (this._el && this._el.addMessage) {
              setTimeout(() => this._injectUpgrade(this._lastLimit, this._lastPeriod), 200);
            } else {
              this._pendingUpgradeCard = true; // deep-chat not mounted yet — flush in _mountDeepChat
            }
          }
        },
          () => {}
        );
      },
      () => {}
    );
  }

  _updateCtxLabel() {
    if (this._ctxLabelRef) this._ctxLabelRef.textContent = this._ctxText();
  }

  // Page told us which item the student is looking at — update silently (no open).
  // A course/exam switch flows through here too, so re-check the remaining count when open.
  _onContext(e) {
    const ctx = e && e.detail && e.detail.context;
    if (!ctx) return;
    const prevExam = this._ctx && this._ctx.exam;
    this._ctx = ctx;
    this._updateCtxLabel();
    if (ctx.exam !== prevExam) this._refreshKilled(ctx.exam); // health probe, not usage
    if (this.state.open && ctx.exam !== prevExam) this._fetchUsage();
  }

  // "Explain my answer" — set context + item, open, and submit the prompt.
  _onExplain(e) {
    if (!this.state.enabled) return;
    if (this.state.killed || isAiTutorKillSwitchedOff()) return; // disabled → don't open/fetch
    const d = (e && e.detail) || {};
    if (d.context) { this._ctx = d.context; this._updateCtxLabel(); }
    if (d.item) this._pendingItem = d.item;
    const text = d.message || 'Explain my answer';
    this._fetchUsage(); // show the current count immediately (the turn's SSE will refine it)
    // small defer: deep-chat warns if submitUserMessage is called before its view renders
    const submit = () => setTimeout(() => this.submitUserMessage({ text: text }), 60);
    if (!this.state.open) this.setState({ open: true }, () => this._ensureMounted(submit));
    else this._ensureMounted(submit);
  }

  _mountDeepChat() {
    if (!this._box || this._el) return;
    const token = getAuthToken();
    const el = document.createElement('deep-chat');

    el.connect = {
      url: TUTOR_URL,
      method: 'POST',
      stream: true,
      headers: { Authorization: token },
    };
    el.requestInterceptor = (req) => {
      // The answered-question context already embeds `context.item`; the "Explain my answer"
      // event also carries a one-shot top-level item — same payload. Send it ONCE: when a
      // pending top-level item exists, strip `item` from a CLONE of the context (never mutate
      // the persistent this._ctx). Backend reads `body.item || body.context.item`, so either
      // placement works; sidebar/typed asks (no top-level item) still ground via context.item.
      let context = this._ctx;
      if (this._pendingItem && context && context.item) {
        context = Object.assign({}, context);
        delete context.item;
      }
      req.body = Object.assign({}, req.body, {
        context: context, sessionId: this._sid, tenantId: 'analystprep',
      });
      // one-shot item payload (e.g. "Explain my answer" → stem/chosen/correct/topic)
      if (this._pendingItem) { req.body.item = this._pendingItem; this._pendingItem = null; }
      return req;
    };
    el.responseInterceptor = (resp) => {
      if (resp && resp.sessionId) this._sid = resp.sessionId;
      // live "N left today" pill (final SSE event carries remaining; null = unlimited)
      if (resp && resp.remaining !== undefined) this._updateUsage(resp.remaining, resp.period);
      // Cap depleted → violet upsell card. Fires (a) on the final event of the turn that
      // spent the last question (remaining === 0), (b) on a rejected attempt (limitReached).
      // _updateUsage above has already cached period / re-armed the flag for this event.
      if (resp && (resp.limitReached === true || resp.remaining === 0) && !this._upgradeShown) {
        this._upgradeShown = true;
        const upPeriod = resp.period || this._lastPeriod;
        // defer past the answer bubble + the extras message (extras inject at ~80ms)
        setTimeout(() => this._injectUpgrade(this._lastLimit, upPeriod), 160);
      }
      // final metadata event → render sources / follow-ups / study-next under the answer.
      // defer so the AI bubble finishes streaming before we append the extras message.
      if (resp && (resp.citations || resp.followups || resp.readmore || resp.messageId)) {
        if (resp.messageId) this._lastMessageId = resp.messageId; // for feedback + escalation
        const extras = {
          citations: resp.citations || [],
          followups: resp.followups || [],
          readmore: resp.readmore || [],
          messageId: resp.messageId || null,
        };
        if (extras.citations.length || extras.followups.length || extras.readmore.length || extras.messageId) {
          setTimeout(() => this._injectExtras(extras), 80);
        }
      }
      // guardrail block → recoverable: offer a human handoff (Intercom) below the refusal bubble
      if (resp && resp.recoverable) {
        const q = resp.question || '';
        setTimeout(() => this._injectRecovery(q), 90);
      }
      // metadata-only chunk (no text) → append nothing, avoid a blank bubble
      if (resp && resp.text == null) return { text: '' };
      return resp;
    };
    // wire clicks inside appended html messages (deep-chat sandboxes inline onclick)
    // READ-NEXT cards without a real URL ask the tutor about the title on click.
    // (Follow-ups are native deep-chat-suggestion-buttons; "Based on" links are plain <a>.)
    el.htmlClassUtilities = {
      'tutor-readmore': { events: { click: (e) => { const r = climbAttr(e.target, 'data-r'); if (r) this.submitUserMessage({ text: r }); } } },
      // recoverable-block card (Change 3): hand off to a human, prefilling Intercom with the blocked question
      'tutor-escalate': { events: { click: (e) => { const q = climbAttr(e.currentTarget, 'data-q'); this.escalateToHuman(q ? 'I asked the AI Tutor: "' + q + '" and it couldn\'t help. Can someone assist?' : 'I need help with a question the AI Tutor couldn\'t answer.'); } } },
      'dc-fb-up': { events: { click: (e) => this._sendFeedback('up', e.currentTarget) } },
      'dc-fb-down': { events: { click: (e) => this._sendFeedback('down', e.currentTarget) } },
      'dc-upgrade-btn': {
        events: {
          click: (e) => {
            try { e.preventDefault(); } catch (err) { /* keep going — worst case the href also navigates */ }
            addUnlimitedToCartAndGo();
          },
        },
      },
    };
    // KaTeX via remarkable (NOT a `math` attribute); service emits $$…$$ display math.
    el.remarkable = { html: true, typographer: true, math: true };
    // themed bubbles / input / send button to match the violet AI-Tutor identity
    el.messageStyles = {
      default: {
        ai: { bubble: { background: T.aiBubbleBg, color: T.aiBubbleText, border: '1px solid ' + T.aiBubbleBorder, borderRadius: '4px 15px 15px 15px', maxWidth: '96%' } },
        user: { bubble: { background: T.userBubbleBg, color: '#fff', borderRadius: '15px 15px 4px 15px', maxWidth: '86%' } },
        upgrade: { bubble: { background: 'none', border: 'none', padding: '0', maxWidth: '100%', width: '100%' } },
        // 'extras' = Based on / READ NEXT / feedback. Same fill as the answer bubble, square
        // top, no top border and a negative top margin so it overlaps and hides the answer
        // bubble's rounded bottom — the two read as ONE block (design Widget_03).
        extras: {
          bubble: {
            background: T.aiBubbleBg, color: T.aiBubbleText,
            border: '1px solid ' + T.aiBubbleBorder, borderTop: 'none',
            borderRadius: '0 0 15px 15px', width: '100%', maxWidth: '96%',
            marginTop: '-11px', paddingTop: '2px',
          },
        },
        // follow-ups live OUTSIDE the bubble, on the panel background
        followups: { bubble: { background: 'none', border: 'none', padding: '0', width: '100%', maxWidth: '100%' } },
        // recoverable-block card (Change 3) — frameless, full width, like the upgrade card
        recovery: { bubble: { background: 'none', border: 'none', padding: '0', width: '100%', maxWidth: '100%' } },
      },
    };
    el.textInput = {
      // initial text only — capped state swaps it via attribute (see _applyComposerState)
      placeholder: { text: INPUT_PLACEHOLDER, style: { color: T.footText } },
      // taller composer to match the design (~52px): left inset + vertical padding on the textarea
      styles: {
        container: { width: '100%', maxWidth: '350px', backgroundColor: T.inputBg, border: '1px solid ' + T.inputBorder, borderRadius: '13px', paddingLeft: '8px', backgroundColor: 'white' },
        text: { color: T.aiBubbleText, fontSize: '14px', paddingTop: '16px', paddingRight: '53px', paddingBottom: '16px', lineHeight: '1.4' },
      },
    };
    el.submitButtonStyles = {
      // keep the button in the active "submit" look (violet bg + white icon) even when the
      // input is empty — otherwise deep-chat renders the greyed "disabled" state (no bg).
      alwaysEnabled: true,
      submit: {
        container: { default: { position: 'absolute', top: '0', bottom: '0', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 'auto', backgroundColor: T.accent, borderRadius: '10px' } },
        // container: { default: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '7px', marginRight: '4px', padding: '7.5px', backgroundColor: T.accent, borderRadius: '10px' } },
        // design send icon = white paper-plane (deep-chat's default is a different arrow).
        // Explicit white stroke + xmlns so deep-chat renders it as-is (no filter needed).
        svg: {
          content:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" ' +
            'fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M22 2 11 13"></path><path d="M22 2 15 22 11 13 2 9 22 2Z"></path></svg>',
          styles: { default: { width: '18px', height: '18px' } },
        },
      },
      // while the request is in flight deep-chat swaps to loading (dots) then stop (cancel).
      // Keep both on the violet button with WHITE glyphs so it never renders as a blank square.
      loading: {
        // no width/height override — the dots indicator isn't square; constraining it squishes
        // it off-centre. Let the flex container centre its natural size; just tint it white.
        // container: { default: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '7px', marginRight: '4px', padding: '7.5px', backgroundColor: T.accent, borderRadius: '10px' } },
        container: { default: { position: 'absolute', top: '0', bottom: '0', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 'auto', backgroundColor: T.accent, borderRadius: '10px' } },
        svg: { styles: { default: { filter: 'brightness(0) invert(1)' } } },
      },
      stop: {
        container: { default: { position: 'absolute', top: '0', bottom: '0', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 'auto', backgroundColor: T.accent, borderRadius: '10px' } },
        // container: { default: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '7px', marginRight: '4px', padding: '7.5px', backgroundColor: T.accent, borderRadius: '10px' } },
        svg: {
          content:
            '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#ffffff">' +
            '<rect x="6" y="6" width="12" height="12" rx="2"></rect></svg>',
          styles: { default: { width: '14px', height: '14px' } },
        },
      },
    };
    // style the markdown rendered inside AI bubbles (headings were cramping together)
    // + the V6 "Indigo" answer-tail classes from the approved spike design (shadow DOM —
    // app-global CSS can't reach in; hover states need this, not inline styles)
    el.auxiliaryStyle =
      'h1,h2,h3,h4{line-height:1.3;margin:14px 0 6px;font-weight:800;color:' + T.aiBubbleText + '}' +
      'h1{font-size:17px}h2{font-size:15px}h3{font-size:13.5px}h4{font-size:12.5px}' +
      'p{margin:7px 0;line-height:1.55}' +
      'ul,ol{margin:7px 0;padding-left:20px}li{margin:3px 0;line-height:1.5}' +
      'hr{margin:12px 0;border:none;border-top:1px solid ' + T.aiBubbleBorder + '}' +
      // --- long formulas / images must scroll INSIDE the bubble, never overflow it ---
      'img{max-width:100%;height:auto;border-radius:8px;margin:6px 0;display:block}' +
      '.katex{max-width:100%}' +
      // katex "display" wrapper variant (spike pattern)
      '.katex-display{overflow-x:auto;overflow-y:hidden;max-width:100%;padding:4px 0;margin:6px 0}' +
      '.katex-display>.katex{white-space:nowrap}' +
      // wrapper-less variant: display-math renders as the paragraph’s only child
      // (<p><span class="katex"><math display="block">) — make that span the scroll container
      'p>.katex:only-child{display:block;overflow-x:auto;overflow-y:hidden;max-width:100%;padding:4px 0}' +
      // --- "Based on N sources" (minimised; hover/focus reveals the source popover) ---
      '.dc-basedon{position:relative;display:inline-flex;margin-top:11px;outline:none}' +
      '.dc-basedon-sum{display:inline-flex;align-items:center;gap:5px;font-weight:600;font-size:0.66rem;color:#8a86a8;cursor:default}' +
      '.dc-basedon-sum strong{font-weight:700;color:#6a6684}' +
      '.dc-basedon-chev{transition:transform .16s ease}' +
      // popover opens UPWARD: the extras message sits at the bottom of the scroller, so there
      // is room above; opening downward would be clipped by the messages container.
      '.dc-basedon-pop{position:absolute;bottom:calc(100% + 7px);left:0;z-index:5;min-width:230px;max-width:290px;background:#fff;border:1px solid #e6e1fb;border-radius:11px;box-shadow:0 10px 30px rgba(40,30,90,.18);padding:9px;opacity:0;visibility:hidden;transform:translateY(4px);transition:opacity .16s ease,transform .16s ease,visibility .16s}' +
      '.dc-basedon:hover .dc-basedon-pop,.dc-basedon:focus-within .dc-basedon-pop{opacity:1;visibility:visible;transform:translateY(0)}' +
      '.dc-basedon:hover .dc-basedon-chev,.dc-basedon:focus-within .dc-basedon-chev{transform:rotate(180deg)}' +
      '.dc-bo-hd{font-weight:800;font-size:0.56rem;letter-spacing:.1em;color:#a8a4c0;padding:2px 6px 7px}' +
      '.dc-bo-item{display:flex;align-items:center;gap:8px;width:100%;box-sizing:border-box;text-align:left;padding:7px 6px;border-radius:8px;text-decoration:none;color:inherit}' +
      'a.dc-bo-item{cursor:pointer}' +
      'a.dc-bo-item:hover{background:#f4f2fe}' +
      '.dc-bo-ic{width:22px;height:22px;flex:none;border-radius:6px;background:#ece9fd;color:#5b46e0;display:flex;align-items:center;justify-content:center}' +
      // video sources get the same filled violet treatment as the READ NEXT play card
      '.dc-bo-ic-play{background:linear-gradient(135deg,#6d4bff,#4a2fe0);color:#fff}' +
      '.dc-bo-ic svg{width:12px;height:12px;flex:none;display:block}' +
      '.dc-bo-lbl{flex:1;min-width:0;font-weight:600;font-size:0.72rem;line-height:1.35;color:#3a3557}' +
      '.dc-bo-go{flex:none;color:#8a86a8;font-weight:800;font-size:0.75rem}' +
      // --- "READ NEXT" label + jump-to-source cards ---
      '.dc-readnext-lbl{font-weight:800;font-size:0.62rem;letter-spacing:.11em;color:#8a86a8;margin:14px 0 1px}' +
      '.dc-srccards{display:flex;flex-direction:column;gap:8px;margin-top:6px}' +
      '.dc-srccard{display:flex;align-items:center;gap:11px;width:100%;box-sizing:border-box;text-align:left;text-decoration:none;color:inherit;padding:10px;border:1px solid #e2ddf8;border-radius:11px;background:#f6f4fe;cursor:pointer;font-family:' + T.font + '}' +
      '.dc-srccard:hover{border-color:#4a2fe0;background:#ece9fd}' +
      '.dc-srcicon{width:34px;height:34px;flex:none;border-radius:9px;background:#ece9fd;color:#4a2fe0;display:flex;align-items:center;justify-content:center}' +
      '.dc-srcicon-play{background:linear-gradient(135deg,#6d4bff,#4a2fe0);color:#fff}' +
      '.dc-srctext{flex:1;min-width:0}' +
      '.dc-srctitle{display:block;font-weight:700;font-size:0.78rem;color:#241f45;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
      '.dc-srcsub{display:block;font-weight:500;font-size:0.69rem;color:#7a7690;margin-top:1px}' +
      '.dc-srccta{flex:none;font-weight:700;font-size:0.72rem;color:#5b46e0}' +
      // --- "SUGGESTED FOLLOW-UPS" (full-width native suggestion buttons; scoped so the
      //     intro chips, which share the class, keep their own inline chip styling) ---
      '.dc-followups{display:flex;flex-direction:column;gap:7px;margin-top:13px}' +
      '.dc-followups-lbl{font-weight:800;font-size:0.62rem;letter-spacing:.1em;color:#8a86a8;margin-bottom:1px}' +
      '.dc-followups .deep-chat-suggestion-button{display:block;width:100%;box-sizing:border-box;text-align:left;background:#f6f4fe;border:1px solid #e2ddf8;color:#4a2fe0;border-radius:11px;padding:11px 13px;font-size:0.8rem;font-weight:700;cursor:pointer;margin:0}' +
      '.dc-followups .deep-chat-suggestion-button:hover{background:#4a2fe0;color:#fff;border-color:#4a2fe0}' +
      // --- "Was this helpful?" feedback row ---
      '.dc-fbrow{display:flex;align-items:center;gap:8px;margin-top:13px;padding-top:11px;border-top:1px solid #e6e1fb}' +
      '.dc-fb-label{font-weight:600;font-size:0.69rem;color:#7a7690}' +
      '.dc-fb{width:34px;height:34px;flex:none;border-radius:8px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;border:1px solid #e2ddf8;background:transparent;color:#7a7690;-webkit-appearance:none;padding:0}' +
      '.dc-fb:hover{border-color:#4a2fe0;color:#4a2fe0}' +
      // iOS Safari does NOT honour an <svg> width/height ATTRIBUTE inside a flex button the
      // way Chrome/Android does — it shrink-wraps the icon to a few px. Pinning the size in
      // CSS (plus flex:none) makes the thumbs identical on iPhone and Android.
      '.dc-fb svg{width:17px;height:17px;flex:none;display:block;pointer-events:none}' +
      // --- taller input composer to match the design (deep-chat's default is shorter) ---
      '.text-input-container{min-height:48px;display:flex;align-items:center}' +
      // composer panel on WHITE (background only — never touch #input's margin/padding/
      // align-items: `margin-top:auto` pins it to the bottom and the default align-items
      // stretch keeps deep-chat's button slots full-height)
      '#input{background-color:' + T.panelBg + ' !important}' +
      // kill every border deep-chat paints on its own containers/host
      ':host,#chat-view,#messages,#input{border:none !important}' +
      // --- capped composer (0 turns left): one dim over box + send button (see _setComposerCapped).
      //     Button keeps pointer-events so the cursor shows; the capture guard kills the submit. ---
      '#input.ap-capped{opacity:.55}' +
      '#input.ap-capped .submit-button{cursor:not-allowed}' +
      // --- limit-reached upsell card ---
      '.dc-upgrade{background:linear-gradient(135deg,#6d4bff,#4a2fe0);border-radius:14px;padding:14px 15px;color:#fff;font-family:' + T.font + '}' +
      '.dc-upgrade-title{font-weight:800;font-size:0.82rem}' +
      '.dc-upgrade-sub{font-weight:500;font-size:0.72rem;line-height:1.45;color:rgba(255,255,255,.85);margin-top:4px}' +
      '.dc-upgrade-sub b{color:#fff}' +
      '.dc-upgrade-row{display:flex;align-items:center;gap:10px;margin-top:11px;flex-wrap:wrap}' +
      '.dc-upgrade-btn{flex:none;background:#fff;color:#4a2fe0;font-weight:800;font-size:0.74rem;text-decoration:none;padding:8px 13px;border-radius:9px;cursor:pointer}' +
      '.dc-upgrade-btn:hover{background:#ece9fd}' +
      '.dc-upgrade-reset{font-weight:600;font-size:0.7rem;color:rgba(255,255,255,.85)}' +
      // --- centre the "typing" loading dots (deep-chat ships asymmetric L/R padding,
      //     1.3em vs .75em, which shoves the three dots off to the right) ---
      '.deep-chat-loading-message-dots-container{padding-inline-start:.85em !important;padding-inline-end:.85em !important;width:.45em !important;box-sizing:content-box}';
    // deep-chat re-applies its own `border:1px solid #cacaca` to the host on connect, which
    // beats a plain inline value — `!important` is what actually removes the grey frame.
    el.style.cssText = 'width:100%;height:100%;background-color:' + T.bodyBg + ';font-family:' + T.font;
    el.style.setProperty('border', 'none', 'important');
    el.innerHTML = introHTML(this._ctx);

    this._box.appendChild(el);
    this._el = el;
    // flush a capped-on-open card that raced ahead of the mount (see _fetchUsage)
    if (this._pendingUpgradeCard) {
      this._pendingUpgradeCard = false;
      setTimeout(() => this._injectUpgrade(this._lastLimit, this._lastPeriod), 250);
    }
    // fresh shadow DOM = no guard; usage may already have resolved as capped before this mount
    this._composerGuard = null;
    this._composerTries = 0;
    this._applyComposerState();
  }

  submitUserMessage(payload) { if (this._el) this._el.submitUserMessage(payload); }
  // append the sources / follow-ups / study-next card after an answer
  _injectExtras(extras) {
    if (!this._el || !this._el.addMessage) return;
    // 1) grounding + actions — glued to the answer bubble (role 'extras')
    const hasAnswerExtras =
      (extras.citations && extras.citations.length) ||
      (extras.readmore && extras.readmore.length) ||
      extras.messageId;
    if (hasAnswerExtras) {
      try { this._el.addMessage({ html: answerExtrasHTML(extras), role: 'extras' }); } catch (e) {}
    }
    // 2) follow-ups — separate frameless block below the bubble
    if (extras.followups && extras.followups.length) {
      try { this._el.addMessage({ html: extrasHTML(extras), role: 'followups' }); } catch (e) {}
    }
  }
  // append the limit-reached upsell card (custom 'upgrade' role = frameless bubble)
  _injectUpgrade(limit, period) {
    if (this._el && this._el.addMessage) {
      try { this._el.addMessage({ html: upgradeHTML(limit, period), role: 'upgrade' }); } catch (e) {}
    }
  }
  // append the recoverable-block card (Change 3) — human handoff after a guardrail refusal
  _injectRecovery(question) {
    if (this._el && this._el.addMessage) {
      try { this._el.addMessage({ html: recoveryHTML(question), role: 'recovery' }); } catch (e) {}
    }
  }
  setContext(ctx) { this._ctx = ctx || {}; }
  // POST 👍/👎 to /tutor/feedback for the answer row carried on the clicked row (data-mid).
  // Visual ack: the chosen thumb fills indigo, the other dims (spike behavior).
  _sendFeedback(rating, btn) {
    const mid = climbAttr(btn, 'data-mid');
    if (!mid) return;
    try {
      fetch(TUTOR_BASE + '/tutor/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthToken() },
        body: JSON.stringify({ messageId: mid, rating: rating }),
      }).catch(() => { /* best-effort */ });
    } catch (e) { /* noop */ }
    try {
      const row = btn.parentNode;
      if (row) {
        const thumbs = row.querySelectorAll('.dc-fb');
        for (let i = 0; i < thumbs.length; i++) { thumbs[i].style.opacity = '0.35'; }
      }
      btn.style.opacity = '1';
      btn.style.background = T.accent;
      btn.style.borderColor = T.accent;
      btn.style.color = '#fff';
    } catch (e) { /* noop */ }
  }

  escalateToHuman(summary) {
    // mark the last answer as escalated (best-effort analytics), then open Intercom
    if (this._lastMessageId) {
      try {
        fetch(TUTOR_BASE + '/tutor/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getAuthToken() },
          body: JSON.stringify({ messageId: this._lastMessageId, escalated: true }),
        }).catch(() => { /* best-effort */ });
      } catch (e) { /* noop */ }
    }
    if (window.Intercom) window.Intercom('showNewMessage', summary || '');
  }

  shouldComponentUpdate(_, n) { return n.enabled !== this.state.enabled || n.open !== this.state.open || n.notice !== this.state.notice || n.killed !== this.state.killed; }

  render() {
    if (!this.state.enabled) return null;
    if (this.state.killed) return null; // admin kill-switch → no panel at all
    const open = this.state.open;

    // Persisted drag offset, applied inline so a moved panel never flashes at its
    // default spot before _settleDragPos() runs. Never offset the mobile sheet.
    const dp = this._dragPos;
    const dragTransform = !isMobileViewport() && (dp.x || dp.y)
      ? 'translate(' + dp.x + 'px,' + dp.y + 'px)'
      : undefined;

    return (
      <div className='ap-tutor-panel' ref={this._setPanelRef} style={{
        // Desktop: anchored top-right, capped height with a ≥96px bottom gap so the panel never
        // covers the bottom-right Intercom bubble. Mobile: the media query below makes it a
        // near-full-screen sheet (Intercom is hidden/dimmed there).
        position: 'fixed', top: 72, right: 16, width: 384, maxWidth: 'calc(100vw - 32px)',
        height: 'calc(100vh - 168px)', maxHeight: 760,
        background: T.panelBg, boxShadow: '0 14px 44px rgba(20,30,80,.20)', zIndex: 1100,
        borderTopLeftRadius: '16',
        borderTopRightRadius: '16',
        overflow: 'hidden',
        display: open ? 'flex' : 'none', flexDirection: 'column', fontFamily: T.font,
        transform: dragTransform,
      }}>
        {/* mobile (<575px): full-height sheet — !important beats the inline desktop sizing */}
        <style dangerouslySetInnerHTML={{ __html:
          // touch-action:none — a touch gesture starting on the header must never pan/scroll
          // the page behind the panel (on mobile the panel isn't draggable, so the header is
          // simply inert; on touch tablets it lets our own drag handler own the gesture).
          '.ap-tutor-grab{cursor:grab;user-select:none;-webkit-user-select:none;touch-action:none;}' +
          // mobile: full-height sheet, NOT draggable — reset any persisted desktop offset
          '@media (max-width:574.98px){.ap-tutor-panel{top:8px !important;right:8px !important;left:8px !important;width:auto !important;height:auto !important;bottom:8px !important;max-height:none !important;border-radius:14px !important;transform:none !important;}' +
          '.ap-tutor-grab{cursor:default;}}' }} />
        {/* Header (gradient) — also the DRAG HANDLE on desktop (grab cursor via .ap-tutor-grab,
            which the mobile media query resets since the sheet isn't draggable) */}
        <div
          ref={this._setDragHandleRef}
          className='ap-tutor-grab'
          onMouseDown={this._onDragStart}
          onTouchStart={this._onDragStart}
          style={{ flex: 'none', background: T.headerBg, padding: '15px 16px 13px', color: '#fff' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(255,255,255,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flex: 'none' }}>✦</span>
            <span style={{ font: '800 15px ' + T.font }}>AnalystPrep AI Tutor</span>
            <span style={{ font: '800 10px ' + T.font, letterSpacing: '.1em', background: '#fff', color: T.headerBadge, padding: '2px 7px', borderRadius: 20 }}>AI</span>
            <button data-no-drag='1' onClick={() => this.setState({ open: false })} style={{ marginLeft: 'auto', border: 'none', background: 'rgba(255,255,255,.18)', color: '#fff', width: 24, height: 24, borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 900, lineHeight: '1' }}>✕</button>
          </div>
          <div style={{ marginTop: 7, font: '500 11px ' + T.font, color: 'rgba(255,255,255,.85)', lineHeight: 1.4 }}>
            AI-generated from AnalystPrep’s CFA curriculum · cites sources · says when unsure.
          </div>
        </div>

        {/* Sub-bar: context + ring counter */}
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: T.subBarBg, borderBottom: '1px solid ' + T.subBarBorder }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1, font: '600 11.5px ' + T.font, color: T.ctxText }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.accent, flex: 'none' }} />
            {/* NOT React-managed: text is set imperatively (ref + _updateCtxLabel) so
                imperative updates never collide with React's text reconciliation. */}
            <span ref={(el) => { this._ctxLabelRef = el; if (el) el.textContent = this._ctxText(); }} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
          </span>
          {!this.state.notice && (
            <span
              ref={(el) => { this._usageRef = el; }}
              title='Questions left today'
              style={{ display: 'none', flex: 'none', alignItems: 'center', gap: 4, font: '700 11px ' + T.font, color: T.accent, background: T.accentSoft, padding: '5px 11px', borderRadius: 20, whiteSpace: 'nowrap' }}
            />
          )}
        </div>

        {/* deep-chat host — OR a friendly notice when the tutor isn’t configured
            for this env / the bundle failed to load. Panel + button still show. */}
        {this.state.notice ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '24px 22px', background: T.bodyBg }}>
            <span style={{ width: 46, height: 46, borderRadius: 12, background: T.accentSoft, color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 12 }}>✦</span>
            <div style={{ font: '800 14px ' + T.font, color: T.aiBubbleText, marginBottom: 6 }}>AI Tutor isn’t available yet</div>
            <div style={{ font: '500 12.5px ' + T.font, color: T.subText, lineHeight: 1.5, maxWidth: 260 }}>
              {this.state.notice === 'disabled'
                ? 'The AI tutor is currently unavailable. Please check back later.'
                : this.state.notice === 'load'
                ? 'The tutor couldn’t load. Please refresh the page and try again.'
                : this.state.notice === 'backend'
                ? 'The AI Tutor service isn’t reachable right now — it may be down or missing its keys. Please try again in a moment.'
                : 'The AI Tutor isn’t configured in this environment yet (missing keys/config). Please try again later or contact support.'}
            </div>
            {this.state.notice !== 'config' && (
              <button
                onClick={() => this.setState({ notice: null }, () => { this._ensureMounted(); this._fetchUsage(); })}
                style={{ marginTop: 14, padding: '8px 16px', border: '1px solid ' + T.chipBorder, borderRadius: 10, background: T.chipBg, color: T.chipText, font: '700 12.5px ' + T.font, cursor: 'pointer' }}
              >
                Try again
              </button>
            )}
          </div>
        ) : null}
        <div ref={this._setRef} style={{ flex: 1, minHeight: 0, display: this.state.notice ? 'none' : 'flex', background: T.bodyBg }} />

        {/* Footer: disclaimer + escalation */}
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 4, padding: '0px 12px 12px', background: T.panelBg }}>
          <span style={{ font: '500 11px ' + T.font, color: T.footText, lineHeight: 1.3 }}>AI can make mistakes — verify with the reading.</span>
          <button onClick={() => this.escalateToHuman('I’d like to talk to a human about this.')} style={{ marginLeft: 'auto', border: 'none', padding: 0, background: 'none', font: '700 11.5px ' + T.font, color: T.accent, cursor: 'pointer', whiteSpace: 'nowrap' }}>Talk to human →</button>
        </div>
      </div>
    );
  }
}

export default AITutorChat;



// WEBPACK FOOTER //
// ./src/components/AITutorChat/index.jsx